/* eslint-disable max-lines */
"use client";

import { FC, useContext, useEffect, useRef, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import Logo from "@/components/common/Logo";
import { ArrowLeftIcon } from "@/components/icons/ArrowLeftIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import UserContext, { UserContextType } from "@/context/UserContext";
import { LayoutData } from "@/types/layoutData";
import { GA4 } from "@/utils/ga4Utils";
import { cn, isProductPath } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import HeaderIcons from "./HeaderIcons";
import styles from "./HeaderSecondary.module.scss";

interface HeaderSecondaryProps {
  pathname: string;
  theme: ThemeTypes;
  layoutDataBySlug?: LayoutData;
}

const HeaderSecondary: FC<HeaderSecondaryProps> = ({
  pathname,
  theme = "master",
  layoutDataBySlug,
}) => {
  const isMouseInHeader = useRef<boolean>(false);
  const userState = useContext<UserContextType>(UserContext); // Type the context
  const [yVal, setYVal] = useState<number>(0);

  const showCart: boolean = layoutDataBySlug?.secondaryHeaderShowCart;
  const showAccount: boolean = layoutDataBySlug?.secondaryHeaderShowAccount !== false;
  let isProductPage: boolean = false;
  let isAccountPage: boolean = false;
  let isIconVisibleOnRightSideOnly: boolean = false;

  if (pathname) {
    isProductPage = isProductPath(pathname);
    isAccountPage = pathname.indexOf("account") > -1;
    isIconVisibleOnRightSideOnly =
      pathname.indexOf("/customer/account/login/") > -1 ||
      pathname.indexOf("/customer/account/create/") > -1;
  }

  const hasSecondaryHeaderLink =
    !isAccountPage &&
    layoutDataBySlug?.secondaryHeaderLinkText &&
    layoutDataBySlug?.secondaryHeaderLinkPath;
  const secondaryHeaderLinkText = layoutDataBySlug?.secondaryHeaderLinkText;
  const secondaryHeaderLinkTextMobile = layoutDataBySlug?.secondaryHeaderLinkTextMobile;

  /*
   * Cart page "Continue Shopping"/"Shop" should route to the men's category
   * when the header is themed for men (driven by the cart's own item-based
   * `site` field), overriding the CMS-configured generic brand category
   * link — for both the desktop and mobile-specific variants.
   */
  const isCartPage = pathname === "/checkout/cart" || pathname === "/checkout/cart/";
  const menCategoryOverride = isCartPage && theme === "men" ? "/hair/men/product/category/" : null;

  const secondaryHeaderLinkPath = menCategoryOverride ?? layoutDataBySlug?.secondaryHeaderLinkPath;
  const secondaryHeaderLinkPathMobile =
    menCategoryOverride ?? layoutDataBySlug?.secondaryHeaderLinkPathMobile;
  const mobileSecondaryHeaderLink =
    secondaryHeaderLinkTextMobile && secondaryHeaderLinkPathMobile
      ? {
          text: secondaryHeaderLinkTextMobile,
          path: secondaryHeaderLinkPathMobile,
        }
      : null;

  useEffect(() => {
    const bodyElem = document.querySelectorAll("body")[0];

    const handleScroll = () => {
      if (!isMouseInHeader.current) {
        if (document.documentElement.scrollTop <= 85) {
          if (bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.remove("pagescrolled");
          }
        } else if (yVal > document.documentElement.scrollTop) {
          if (bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.remove("pagescrolled");
          }
        } else {
          if (!bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.add("pagescrolled");
          }
        }
        setYVal(document.documentElement.scrollTop);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [yVal]);

  let href: string =
    userState?.site === "men" ? "/hair/men/product/category/" : "/hair/brand/product/category/";

  // For non-account pages, use secondaryHeaderLinkPath if available (single-link layout only)
  if (!isAccountPage && hasSecondaryHeaderLink && secondaryHeaderLinkPath) {
    href = secondaryHeaderLinkPath;
  }

  return (
    <header
      data-mousedown // Custom data attribute
      className={cn(styles["header-wrap"], "header-secondary")}
      onMouseEnter={() => (isMouseInHeader.current = true)}
      onMouseLeave={() => (isMouseInHeader.current = false)}
      onTouchEnd={() =>
        setTimeout(() => {
          isMouseInHeader.current = false;
        })
      }
    >
      <div className={styles["header-container"]}>
        <div className="contents-container h-full">
          <div className={cn(styles["simple-flex"])}>
            <div className={"left-wrap w-3/10 self-center flex-1"}>
              {hasSecondaryHeaderLink &&
                (mobileSecondaryHeaderLink ? (
                  <>
                    <LinkHandler
                      path={mobileSecondaryHeaderLink.path}
                      onClick={() =>
                        GA4.onMarketingCtaClicked({
                          destination: mobileSecondaryHeaderLink.path,
                          text: mobileSecondaryHeaderLink.text,
                          type: "link",
                        })
                      }
                      className={cn(styles["reverse-arrow-link"], "md:hidden")}
                    >
                      <TextElementNova element="span" className={"CTA-10-Dia-Normal flex"}>
                        <div className={styles["arrow-flex"]}>
                          {(layoutDataBySlug?.secondaryHeaderLinkArrow ||
                            mobileSecondaryHeaderLink.text === "Shop") && (
                            <div>
                              <ArrowLeftIcon />
                            </div>
                          )}
                          <div>{mobileSecondaryHeaderLink.text}</div>
                        </div>
                      </TextElementNova>
                    </LinkHandler>
                    <LinkHandler
                      path={secondaryHeaderLinkPath!}
                      onClick={() =>
                        GA4.onMarketingCtaClicked({
                          destination: secondaryHeaderLinkPath!,
                          text: secondaryHeaderLinkText!,
                          type: "link",
                        })
                      }
                      className={cn(styles["reverse-arrow-link"], "hidden md:flex")}
                    >
                      <TextElementNova element="span" className={"CTA-10-Dia-Normal flex"}>
                        <div className={styles["arrow-flex"]}>
                          {layoutDataBySlug?.secondaryHeaderLinkArrow && (
                            <div>
                              <ArrowLeftIcon />
                            </div>
                          )}
                          <div>{secondaryHeaderLinkText}</div>
                        </div>
                      </TextElementNova>
                    </LinkHandler>
                  </>
                ) : (
                  <LinkHandler
                    path={href}
                    onClick={() =>
                      GA4.onMarketingCtaClicked({
                        destination: href,
                        text: secondaryHeaderLinkText ?? "",
                        type: "link",
                      })
                    }
                    className={styles["reverse-arrow-link"]}
                  >
                    {/* Mobile View */}
                    <TextElementNova element="span" className={"CTA-10-Dia-Normal flex md:hidden"}>
                      <div className={styles["arrow-flex"]}>
                        {layoutDataBySlug?.secondaryHeaderLinkArrow && (
                          <div>
                            <ArrowLeftIcon />
                          </div>
                        )}
                        <div>{secondaryHeaderLinkText}</div>
                      </div>
                    </TextElementNova>

                    {/* Desktop View */}
                    <TextElementNova element="span" className={"CTA-10-Dia-Normal hidden md:flex"}>
                      <div className={styles["arrow-flex"]}>
                        {layoutDataBySlug?.secondaryHeaderLinkArrow && (
                          <div>
                            <ArrowLeftIcon />
                          </div>
                        )}
                        <div>{secondaryHeaderLinkText}</div>
                      </div>
                    </TextElementNova>
                  </LinkHandler>
                ))}
            </div>

            <div className={"w-4/10 self-center center-wrap max-w-[141px] lg:max-w-[156px]"}>
              <Logo isSimple={true} theme={theme} />
            </div>

            <div className={"right-wrap w-3/10 self-center flex-1"}>
              <div className={`flex flex-wrap items-center justify-end mt-[1px]`}>
                <HeaderIcons
                  theme={theme}
                  isSimple={true}
                  isIconVisibleOnRightSideOnly={isIconVisibleOnRightSideOnly}
                  isProductPage={isProductPage}
                  isAccountPage={isAccountPage || !showAccount}
                  showCart={showCart}
                  pathname={pathname}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderSecondary;
